/**
 * Console Error Buffer
 *
 * Captures console.error and console.warn into a ring buffer for inclusion
 * in feedback emails. Initialized on import so we capture errors before the
 * user opens the feedback dialog.
 */

const MAX_ENTRIES = 10
const MAX_STACK_CHARS = 800
const MAX_TOTAL_CHARS = 4000

interface BufferEntry {
  level: 'error' | 'warn'
  message: string
  timestamp: string
  stack?: string
}

const buffer: BufferEntry[] = []
let originalError: typeof console.error
let originalWarn: typeof console.warn

function truncate(str: string, max: number): string {
  if (str.length <= max) return str
  return str.slice(0, max) + '...'
}

function capture(level: 'error' | 'warn', args: unknown[]): void {
  const message = args
    .map((a) => (typeof a === 'object' && a !== null && 'stack' in a
      ? (a as Error).message ?? String(a)
      : String(a)))
    .join(' ')

  let stack: string | undefined
  const firstArg = args[0]
  if (firstArg instanceof Error && firstArg.stack) {
    stack = truncate(firstArg.stack, MAX_STACK_CHARS)
  }

  buffer.push({
    level,
    message: truncate(message, 500),
    timestamp: new Date().toISOString(),
    stack,
  })

  if (buffer.length > MAX_ENTRIES) {
    buffer.shift()
  }
}

function install(): void {
  if (originalError) return

  originalError = console.error
  originalWarn = console.warn

  console.error = (...args: unknown[]) => {
    capture('error', args)
    originalError.apply(console, args)
  }

  console.warn = (...args: unknown[]) => {
    capture('warn', args)
    originalWarn.apply(console, args)
  }
}

install()

/**
 * Returns a formatted string of captured console errors for inclusion in feedback.
 * Truncated to MAX_TOTAL_CHARS to avoid huge payloads.
 */
export function getConsoleErrorBuffer(): string {
  if (buffer.length === 0) {
    return '(no console errors captured)'
  }

  const lines: string[] = []
  let totalLength = 0

  for (const entry of buffer) {
    const line1 = `[${entry.timestamp}] ${entry.level.toUpperCase()}: ${entry.message}`
    const line2 = entry.stack ? `  ${entry.stack}` : ''

    const block = line2 ? `${line1}\n${line2}` : line1
    if (totalLength + block.length > MAX_TOTAL_CHARS) {
      lines.push('... (truncated)')
      break
    }
    lines.push(block)
    totalLength += block.length
  }

  return lines.join('\n\n')
}
